import { formatPhone } from "shared/utils/format-phone";
import { FieldDisplaySetting } from "shared/types";

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

export const isFieldVisible = (
  value: unknown,
  setting?: FieldDisplaySetting
): boolean => hasValue(value) || setting !== FieldDisplaySetting.Hidden;

export const isStepVisible = (
  group?: Record<string, FieldDisplaySetting | boolean | undefined>
): boolean =>
  Object.values(group ?? {}).some(
    (setting) => setting !== FieldDisplaySetting.Hidden
  );

/** "primary_residence" -> "Primary residence", "married" -> "Married". */
export const formatLabel = (value?: string | null): string => {
  const raw = (value ?? "").trim();
  if (!raw) return "—";
  const spaced = raw.replace(/_/g, " ").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** ISO date -> "Mar 14, 1988". Falls back to the raw value when unparseable. */
export const formatDate = (value?: string | null): string => {
  const raw = (value ?? "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

/** ISO date -> "Jun 2021" — for employment ranges where the day is noise. */
export const formatMonthYear = (value?: string | null): string => {
  const raw = (value ?? "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

/** ISO timestamp -> "May 28, 2026 at 4:39 PM". */
export const formatTimestamp = (value?: string | null): string => {
  const raw = (value ?? "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
  return `${date} at ${time}`;
};

/** 27 -> "2 yr 3 mo", 11 -> "11 mo", 24 -> "2 yr". */
export const formatTenure = (months?: number | null): string => {
  if (!months || months < 0) return "—";
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts: string[] = [];
  if (years) parts.push(`${years} yr`);
  if (rest) parts.push(`${rest} mo`);
  return parts.length ? parts.join(" ") : "0 mo";
};

export const formatPhoneOrDash = (value?: string | null): string => {
  const raw = (value ?? "").trim();
  return raw ? formatPhone(raw) : "—";
};
